import React from 'react';
import { SxProps } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

import HomeBottomButtonOptionsSettings from '@core/dashboard/components/HomeBottomBar/HomeBottomButtonOptionsSettings';
import { useMytaverse } from '@providers/MytaverseProvider';
import { getAiAssistantLanguageOptions } from '@helpers/assistant';
import PulsingBorder from '@components/PulsingBorder';
import { TutorialHighlightKey, useTutorialHighlight } from '@core/tutorials';
import { env } from 'env';

const { REACT_APP_FEATURE_SHOW_LANGUAGE = 'true' } = env;
const showLanguage = REACT_APP_FEATURE_SHOW_LANGUAGE === 'true';

interface LanguageSelectorProps {
  label: string;
  sx?: SxProps;
}

/**
 * Dropdown arrow for the language `Select`, wrapped in a `PulsingBorder` that
 * activates while the guided tutorial highlights this control. Defined at
 * module scope (rather than inline in `LanguageSelector`) so its identity is
 * stable across renders — MUI's `Select` remounts its `IconComponent` slot
 * whenever that reference changes.
 *
 * MUI's own arrow (`KeyboardArrowDown` as a bare `SvgIcon`) carries the
 * `.MuiSelect-icon` positioning class directly on the `<svg>`, whose
 * `top: calc(50% - .5em)` vertical-centering resolves against that svg's own
 * `font-size: 1.5rem` (MUI's default `SvgIcon` size). Once wrapped, this `div`
 * — not the nested svg — is what receives that class, so it needs the same
 * `font-size` explicitly or the `.5em` resolves against an inherited font
 * size instead and the arrow sits off-center. `right: 6` restores the same
 * horizontal nudge the select's own styles used to apply directly to the
 * (then-absolutely-positioned) svg.
 *
 * @param props.className - Forwarded by MUI `Select`; carries the arrow's positioning classes.
 */
const LanguageDropdownIcon = ({ className }: { className?: string }) => {
  const isHighlighted = useTutorialHighlight(
    TutorialHighlightKey.LanguageDropdown,
  );

  return (
    <PulsingBorder
      active={isHighlighted}
      borderRadius="50%"
      className={className}
      sx={{ fontSize: '1.5rem', right: 6 }}
    >
      <KeyboardArrowDown />
    </PulsingBorder>
  );
};

/**
 * Renders the "Language:" dropdown shown in the Experience settings tab and
 * the home bottom bar, sourced from the map's configured AI-assistant
 * languages.
 *
 * @param props.label - Label rendered above the select.
 * @param props.sx - Additional `sx` overrides merged onto the select.
 */
const LanguageSelector = ({ label, sx }: LanguageSelectorProps) => {
  const { appSettings, selectLanguage, selectedLanguage } = useMytaverse();

  const languageOptions = getAiAssistantLanguageOptions(
    appSettings?.aiAssistant,
  );

  if (!showLanguage || !languageOptions?.length) {
    return null;
  }

  return (
    <HomeBottomButtonOptionsSettings
      devices={languageOptions.map(({ label: l, key }) => ({
        label: l,
        deviceId: key,
      }))}
      handleSelect={(e) => selectLanguage(e.target.value)}
      label={label}
      selectedValue={selectedLanguage}
      sx={sx}
      testId="user-profile-language-select"
      IconComponent={LanguageDropdownIcon}
    />
  );
};

export default LanguageSelector;
